import { useState, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useFiscalYear } from '../context/FiscalYearContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import logger from '../utils/logger';
import { API_URL } from '../utils/apiConfig';
import { optimisticSave } from '../utils/optimisticSave';
import {
  Car, Flame, Factory, Wind, Snowflake,
  Trash2, Truck, Plane, Home, Building,
  ShoppingCart, Key, Store, PiggyBank,
} from 'lucide-react';

const INITIAL_WIZARD_ANSWERS = {
  hasVehicles: null, hasCombustion: null, hasFugitiveEmissions: null,
  hasProcessEmissions: null, usesHeating: null, usesCooling: null,
  hasWaste: null, hasFreight: null, hasBusinessTravel: null,
  hasCommuting: null, hasLeasedAssetsUpstream: null, sellsProducts: null,
  hasDownstreamTransport: null, hasLeasedAssetsDownstream: null,
  hasFranchises: null, hasInvestments: null,
};

const PRODUCT_CATEGORIES = ['transformation_produits', 'utilisation_produits', 'fin_vie_produits'];

export const useGeneralInfo = () => {
  const { t, language } = useLanguage();
  const { currentFiscalYear: selectedFiscalYear, fiscalYears, applyFreshFiscalYear } = useFiscalYear();
  // Campagne #3 A2 : PUT /fiscal-years/{id}/context = require_fy_modify_state
  // (cs_admin OU admin). Couvre contexte (employees/revenue/surface) ET
  // périmètre/wizard (excluded_categories) — tous via ce même endpoint.
  const { isCsAdmin, isCompanyAdmin } = useAuth();
  const canEditContext = isCsAdmin || isCompanyAdmin;

  const [company, setCompany] = useState({
    name: '', location: '', sector: '',
    entity_type: 'private_company',
    consolidation_approach: 'operational_control',
    fiscal_year_start_month: 1,
  });
  const [fiscalYearContext, setFiscalYearContext] = useState({
    employees: 0, revenue: 0, surface_area: 0, excluded_categories: [],
  });
  const [contextLoading, setContextLoading] = useState(false);
  const [contextReadonly, setContextReadonly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingContext, setSavingContext] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [savedContext, setSavedContext] = useState(false);
  const [savedPerimeter, setSavedPerimeter] = useState(false);
  const [savedWizard, setSavedWizard] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showWizard, setShowWizard] = useState(false);
  const [showManualConfig, setShowManualConfig] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [wizardAnswers, setWizardAnswers] = useState(INITIAL_WIZARD_ANSWERS);

  const fetchData = useCallback(async () => {
    try {
      const [companyRes, categoriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/companies`),
        axios.get(`${API_URL}/api/categories`),
      ]);
      if (companyRes.data) {
        setCompany({
          name: companyRes.data.name || '', location: companyRes.data.location || '',
          sector: companyRes.data.sector || '',
          entity_type: companyRes.data.entity_type || 'private_company',
          consolidation_approach: companyRes.data.consolidation_approach || 'operational_control',
          fiscal_year_start_month: companyRes.data.fiscal_year_start_month || 1,
          id: companyRes.data.id,
        });
      }
      setCategories(categoriesRes.data || []);
    } catch (error) {
      logger.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFiscalYearContext = useCallback(async (fiscalYearId) => {
    setContextLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/fiscal-years/${fiscalYearId}/context`);
      const ctx = response.data.context || {};
      setFiscalYearContext({
        employees: ctx.employees || 0, revenue: ctx.revenue || 0,
        surface_area: ctx.surface_area || 0,
        excluded_categories: ctx.excluded_categories || [],
      });
      setContextReadonly(response.data.is_readonly || false);
      // Réparation post-409 VRAI conflit : le GET expose updated_at (4d6412d) —
      // resync du sentinel par merge PARTIEL {id, updated_at} (le GET ne renvoie
      // pas le doc FY complet). Inoffensif au mount/changement FY : GET = état
      // frais. fiscalYearId (pas selectedFiscalYear.id) : le setter ne touche
      // que le FY réellement fetché, même si l'user a switché entre-temps.
      if (response.data.updated_at) {
        applyFreshFiscalYear({ id: fiscalYearId, updated_at: response.data.updated_at });
      }
    } catch (error) {
      logger.error('Failed to fetch fiscal year context:', error);
    } finally {
      setContextLoading(false);
    }
  }, [applyFreshFiscalYear]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (company.id) {
        await axios.put(`${API_URL}/api/companies`, company);
      } else {
        const response = await axios.post(`${API_URL}/api/companies`, company);
        setCompany(response.data);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      logger.error('Failed to save:', error);
      setSaveError(true);
      setTimeout(() => setSaveError(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveContext = async () => {
    if (!selectedFiscalYear?.id || contextReadonly || !canEditContext) return;
    setSavingContext(true);
    try {
      const result = await optimisticSave(t, () =>
        axios.put(`${API_URL}/api/fiscal-years/${selectedFiscalYear.id}/context`, {
          ...fiscalYearContext,
          updated_at: selectedFiscalYear?.updated_at,
        })
      );
      if (!result.ok) {
        fetchFiscalYearContext(selectedFiscalYear.id);
        return;
      }
      // FIX 409 sentinel périmé : le PUT renvoie le FY frais (return AFTER) —
      // resync updated_at en mémoire, sinon le 2e save 409 jusqu'au F5.
      applyFreshFiscalYear(result.data);
      setSavedContext(true);
      setTimeout(() => setSavedContext(false), 2000);
    } catch (error) {
      logger.error('Failed to save fiscal year context:', error);
    } finally {
      setSavingContext(false);
    }
  };

  const toggleCategory = async (categoryCode) => {
    if (contextReadonly || !canEditContext) return;
    const newExcluded = fiscalYearContext.excluded_categories?.includes(categoryCode)
      ? fiscalYearContext.excluded_categories.filter(c => c !== categoryCode)
      : [...(fiscalYearContext.excluded_categories || []), categoryCode];
    setFiscalYearContext(prev => ({ ...prev, excluded_categories: newExcluded }));
    try {
      const result = await optimisticSave(t, () =>
        axios.put(`${API_URL}/api/fiscal-years/${selectedFiscalYear.id}/context`, {
          ...fiscalYearContext, excluded_categories: newExcluded,
          updated_at: selectedFiscalYear?.updated_at,
        })
      );
      if (!result.ok) {
        fetchFiscalYearContext(selectedFiscalYear.id);
        return;
      }
      applyFreshFiscalYear(result.data); // FIX 409 sentinel périmé
      setSavedPerimeter(true);
      setTimeout(() => setSavedPerimeter(false), 2000);
    } catch (error) {
      logger.error('Failed to auto-save category:', error);
    }
  };

  const toggleProductCategories = async () => {
    if (contextReadonly || !canEditContext) return;
    const allIncluded = PRODUCT_CATEGORIES.every(
      code => !fiscalYearContext.excluded_categories?.includes(code),
    );
    const newExcluded = allIncluded
      ? [...(fiscalYearContext.excluded_categories || []), ...PRODUCT_CATEGORIES]
      : (fiscalYearContext.excluded_categories || []).filter(c => !PRODUCT_CATEGORIES.includes(c));
    setFiscalYearContext(prev => ({ ...prev, excluded_categories: newExcluded }));
    try {
      const result = await optimisticSave(t, () =>
        axios.put(`${API_URL}/api/fiscal-years/${selectedFiscalYear.id}/context`, {
          ...fiscalYearContext, excluded_categories: newExcluded,
          updated_at: selectedFiscalYear?.updated_at,
        })
      );
      if (!result.ok) {
        fetchFiscalYearContext(selectedFiscalYear.id);
        return;
      }
      applyFreshFiscalYear(result.data); // FIX 409 sentinel périmé
      setSavedPerimeter(true);
      setTimeout(() => setSavedPerimeter(false), 2000);
    } catch (error) {
      logger.error('Failed to auto-save categories:', error);
    }
  };

  const areProductCategoriesIncluded = () =>
    PRODUCT_CATEGORIES.every(code => !fiscalYearContext.excluded_categories?.includes(code));

  // ---- Wizard ----
  const wizardSteps = [
    { id: 'intro', title: t('generalInfo.wizard.intro.title'), subtitle: t('generalInfo.wizard.intro.subtitle'), questions: [] },
    { id: 'scope1', title: t('generalInfo.wizard.scope1.title'), subtitle: t('generalInfo.wizard.scope1.subtitle'), questions: [
      { key: 'hasVehicles', icon: Car, text: t('generalInfo.wizard.q.hasVehicles.text'), hint: t('generalInfo.wizard.q.hasVehicles.hint'), categories: ['combustion_mobile'] },
      { key: 'hasCombustion', icon: Flame, text: t('generalInfo.wizard.q.hasCombustion.text'), hint: t('generalInfo.wizard.q.hasCombustion.hint'), categories: ['combustion_fixe'] },
      { key: 'hasFugitiveEmissions', icon: Factory, text: t('generalInfo.wizard.q.hasFugitiveEmissions.text'), hint: t('generalInfo.wizard.q.hasFugitiveEmissions.hint'), categories: ['emissions_fugitives'] },
      { key: 'hasProcessEmissions', icon: Wind, text: t('generalInfo.wizard.q.hasProcessEmissions.text'), hint: t('generalInfo.wizard.q.hasProcessEmissions.hint'), categories: ['emissions_procedes'] },
    ]},
    { id: 'scope2', title: t('generalInfo.wizard.scope2.title'), subtitle: t('generalInfo.wizard.scope2.subtitle'), questions: [
      { key: 'usesHeating', icon: Flame, text: t('generalInfo.wizard.q.usesHeating.text'), hint: t('generalInfo.wizard.q.usesHeating.hint'), categories: ['chaleur_vapeur'] },
      { key: 'usesCooling', icon: Snowflake, text: t('generalInfo.wizard.q.usesCooling.text'), hint: t('generalInfo.wizard.q.usesCooling.hint'), categories: ['refroidissement'] },
    ]},
    { id: 'scope3_amont', title: t('generalInfo.wizard.scope3_amont.title'), subtitle: t('generalInfo.wizard.scope3_amont.subtitle'), questions: [
      { key: 'hasWaste', icon: Trash2, text: t('generalInfo.wizard.q.hasWaste.text'), hint: t('generalInfo.wizard.q.hasWaste.hint'), categories: ['dechets_operations'] },
      { key: 'hasFreight', icon: Truck, text: t('generalInfo.wizard.q.hasFreight.text'), hint: t('generalInfo.wizard.q.hasFreight.hint'), categories: ['transport_distribution_amont'] },
      { key: 'hasBusinessTravel', icon: Plane, text: t('generalInfo.wizard.q.hasBusinessTravel.text'), hint: t('generalInfo.wizard.q.hasBusinessTravel.hint'), categories: ['deplacements_professionnels'] },
      { key: 'hasCommuting', icon: Home, text: t('generalInfo.wizard.q.hasCommuting.text'), hint: t('generalInfo.wizard.q.hasCommuting.hint'), categories: ['deplacements_domicile_travail'] },
      { key: 'hasLeasedAssetsUpstream', icon: Building, text: t('generalInfo.wizard.q.hasLeasedAssetsUpstream.text'), hint: t('generalInfo.wizard.q.hasLeasedAssetsUpstream.hint'), categories: ['actifs_loues_amont'] },
    ]},
    { id: 'scope3_aval', title: t('generalInfo.wizard.scope3_aval.title'), subtitle: t('generalInfo.wizard.scope3_aval.subtitle'), questions: [
      { key: 'sellsProducts', icon: ShoppingCart, text: t('generalInfo.wizard.q.sellsProducts.text'), hint: t('generalInfo.wizard.q.sellsProducts.hint'), categories: ['transformation_produits', 'utilisation_produits', 'fin_vie_produits'] },
      { key: 'hasDownstreamTransport', icon: Truck, text: t('generalInfo.wizard.q.hasDownstreamTransport.text'), hint: t('generalInfo.wizard.q.hasDownstreamTransport.hint'), categories: ['transport_distribution_aval'] },
      { key: 'hasLeasedAssetsDownstream', icon: Key, text: t('generalInfo.wizard.q.hasLeasedAssetsDownstream.text'), hint: t('generalInfo.wizard.q.hasLeasedAssetsDownstream.hint'), categories: ['actifs_loues_aval'] },
      { key: 'hasFranchises', icon: Store, text: t('generalInfo.wizard.q.hasFranchises.text'), hint: t('generalInfo.wizard.q.hasFranchises.hint'), categories: ['franchises'] },
      { key: 'hasInvestments', icon: PiggyBank, text: t('generalInfo.wizard.q.hasInvestments.text'), hint: t('generalInfo.wizard.q.hasInvestments.hint'), categories: ['investissements'] },
    ]},
    { id: 'summary', title: t('generalInfo.wizard.summary.title'), subtitle: t('generalInfo.wizard.summary.subtitle'), questions: [] },
  ];

  const handleWizardAnswer = (key, value) => setWizardAnswers(prev => ({ ...prev, [key]: value }));

  const getSelectedCategoriesFromWizard = () => {
    const selected = new Set();
    wizardSteps.forEach(step => {
      step.questions?.forEach(q => {
        if (wizardAnswers[q.key] === true) q.categories.forEach(cat => selected.add(cat));
      });
    });
    selected.add('electricite');
    selected.add('biens_services_achetes');
    selected.add('biens_equipement');
    const scope1or2 = ['combustion_mobile', 'combustion_fixe', 'emissions_fugitives', 'emissions_procedes', 'electricite', 'chaleur_vapeur', 'refroidissement'];
    if (scope1or2.some(cat => selected.has(cat))) selected.add('activites_combustibles_energie');
    return selected;
  };

  const applyWizardResults = async () => {
    const selectedCategories = getSelectedCategoriesFromWizard();
    const allCategoryCodes = categories.map(c => c.code);
    const newExcluded = allCategoryCodes.filter(code => !selectedCategories.has(code));
    setFiscalYearContext(prev => ({ ...prev, excluded_categories: newExcluded }));
    if (selectedFiscalYear?.id && !contextReadonly && canEditContext) {
      try {
        const result = await optimisticSave(t, () =>
          axios.put(`${API_URL}/api/fiscal-years/${selectedFiscalYear.id}/context`, {
            ...fiscalYearContext, excluded_categories: newExcluded,
            updated_at: selectedFiscalYear?.updated_at,
          })
        );
        if (!result.ok) {
          fetchFiscalYearContext(selectedFiscalYear.id);
          setShowWizard(false);
          setWizardStep(0);
          return;
        }
        applyFreshFiscalYear(result.data); // FIX 409 sentinel périmé
      } catch (error) {
        logger.error('Failed to save wizard results:', error);
      }
    }
    setShowWizard(false);
    setWizardStep(0);
    setSavedWizard(true);
    setTimeout(() => setSavedWizard(false), 2000);
  };

  const resetWizard = () => { setWizardStep(0); setWizardAnswers(INITIAL_WIZARD_ANSWERS); };
  const openWizard = () => { resetWizard(); setShowWizard(true); };

  const isPrivateCompany = company.entity_type === 'private_company';

  const entityTypes = [
    { value: 'private_company', label: t('generalInfo.entityTypes.privateCompany') },
    { value: 'public_admin', label: t('generalInfo.entityTypes.publicAdmin') },
    { value: 'association', label: t('generalInfo.entityTypes.association') },
    { value: 'foundation', label: t('generalInfo.entityTypes.foundation') },
    { value: 'other', label: t('generalInfo.entityTypes.other') },
  ];

  const sectors = [
    'manufacturing', 'services', 'technology', 'retail', 'construction',
    'transport', 'energy', 'agriculture', 'healthcare', 'education',
    'finance', 'hospitality', 'other',
  ];

  const consolidationApproaches = [
    { value: 'operational_control', label: t('company.operationalControl') },
    { value: 'financial_control', label: t('company.financialControl') },
    { value: 'equity_share', label: t('company.equityShare') },
  ];

  const scopeCategories = {
    scope1: categories.filter(c => c.scope === 'scope1'),
    scope2: categories.filter(c => c.scope === 'scope2'),
    scope3_amont: categories.filter(c => c.scope === 'scope3_amont'),
    scope3_aval: categories.filter(c => c.scope === 'scope3_aval'),
  };

  return {
    // data
    company, setCompany, fiscalYearContext, setFiscalYearContext,
    categories, selectedFiscalYear, fiscalYears, language,
    // status
    loading, saving, savingContext, saved, savedContext, saveError,
    savedPerimeter, savedWizard, contextLoading, contextReadonly,
    canEditContext,
    // actions
    fetchData, fetchFiscalYearContext, handleSave, handleSaveContext,
    toggleCategory, toggleProductCategories, areProductCategoriesIncluded,
    // wizard
    showWizard, setShowWizard, showManualConfig, setShowManualConfig,
    wizardStep, setWizardStep, wizardAnswers, wizardSteps,
    handleWizardAnswer, getSelectedCategoriesFromWizard,
    applyWizardResults, openWizard,
    // derived
    isPrivateCompany, entityTypes, sectors, consolidationApproaches,
    scopeCategories, PRODUCT_CATEGORIES,
    // i18n
    t,
  };
};
